import { useEffect, useRef, useState } from 'react';
import { Toast as RadixToast } from 'radix-ui';
import { Toast } from '@/components/ui/Toast';
import { useToastQueue } from '@/hooks/useToastQueue';

// Matches Toast.tsx's `duration-200` exit transition - removing an item from
// the queue immediately (rather than after this) would unmount it mid-fade,
// so the close animation never gets a chance to play.
const EXIT_ANIMATION_MS = 200;

/**
 * App-wide toast host. Mounted once in App.tsx: owns the Radix Toast
 * Provider + Viewport and renders the live queue (`useToastQueue`). Feature
 * code never renders `Toast` directly - call `toast.success`/`toast.error`
 * (`src/lib/toast.ts`).
 */
export function Toaster() {
  const { toasts, dismiss } = useToastQueue();
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
  const pendingTimeouts = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    // A toast can only leave `toasts` via dismiss(), which we always route
    // through the closing-flag delay below - so once an id is gone from the
    // queue, its flag is stale and safe to drop.
    setClosingIds((prev) => {
      const stillQueued = new Set([...prev].filter((id) => toasts.some((item) => item.id === id)));
      return stillQueued.size === prev.size ? prev : stillQueued;
    });
  }, [toasts]);

  // Toaster is a permanent, once-mounted fixture (App.tsx), but clear any
  // in-flight exit timers on unmount anyway rather than leaking them.
  useEffect(() => {
    const timeouts = pendingTimeouts.current;
    return () => timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  }, []);

  const requestClose = (id: string) => {
    setClosingIds((prev) => new Set(prev).add(id));
    const timeoutId = setTimeout(() => {
      pendingTimeouts.current.delete(id);
      dismiss(id);
    }, EXIT_ANIMATION_MS);
    pendingTimeouts.current.set(id, timeoutId);
  };

  return (
    <RadixToast.Provider swipeDirection="right" duration={5000}>
      {toasts.map((item) => (
        <Toast
          key={item.id}
          open={!closingIds.has(item.id)}
          onOpenChange={(open) => {
            if (!open) requestClose(item.id);
          }}
          variant={item.variant}
          title={item.message}
        />
      ))}
      <RadixToast.Viewport className="fixed right-0 bottom-0 z-50 flex w-full max-w-sm flex-col gap-2 p-6 outline-none" />
    </RadixToast.Provider>
  );
}
